import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot } from '@angular/router';
import { Injectable } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { Observable, of, combineLatest } from 'rxjs';
import { map, switchMap, tap, take, timeout, catchError } from 'rxjs/operators';
import { CookieService } from 'ngx-cookie-service';
import { Role } from '../enums/role';
import { getAvailablePractices } from '../practices/state/selectors';
import { getConversation } from '../conversation/state/selectors';
import { AppState } from '../state/reducers';
import * as PracticeActions from '../practices/state/actions';
import * as ConversationActions from '../conversation/state/actions';

@Injectable({
  providedIn: 'root'
})
export class ConversationCanBeViewedByAnalysts {
  constructor(
    private store: Store<AppState>,
    private cookieService: CookieService,
    private router: Router
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> {
    const conversationId = route.paramMap.get('id');
    if (!conversationId || conversationId === '0') {
      return of(true);
    }

    // Get conversation from store
    const conversation$ = this.store.pipe(
      select(getConversation),
      map((data: any) => {
        if (data && !Array.isArray(data) && data.id === conversationId) {
          return data;
        }
        return null;
      })
    );

    // Get practices from store
    const practices$ = this.store.pipe(
      select(getAvailablePractices),
      map((practices: any) => Array.isArray(practices) ? practices : [])
    );

    return combineLatest([conversation$, practices$]).pipe(
      tap(([conv, practices]) => {
        // Dispatch actions only if data is missing
        if (!conv) {
          this.store.dispatch(ConversationActions.GetConversation({ id: conversationId }));
        }
        if (!practices || practices.length === 0) {
          //this.store.dispatch(PracticeActions.GetUserPractices());
        }
      }),
      // Wait briefly for data to load if needed
      switchMap(([conv, practices]) => {
        if (conv && practices && practices.length > 0) {
          return of([conv, practices]);
        }
        // Give it a moment for the effects to load data, then try again
        return combineLatest([conversation$, practices$]).pipe(
          take(1),
          
          catchError(() => of([null, []]))
        );
      }),
      map(([conv, practices]) => {
        // If no conversation found, allow navigation (let component handle 404)
        if (!conv) {
          return true;
        }

        // If no practices, deny access
        if (!practices || practices.length === 0) {
          return false;
        }

        // If conversation is resolved, allow access
        if (conv.resolvedAt) {
          return true;
        }

        // Check practice role for unresolved conversations
        let practice: any;
        const cookiePractice = this.cookieService.get('messaging-selected-practice');

        if (cookiePractice) {
          practice = practices.find((p: any) => p.id === cookiePractice);
        }

        if (!practice && practices.length > 0) {
          practice = practices[0];
        }

        // Allow access if user is not an analyst
        return !!(practice && practice.userRole !== Role.ANALYST);
      }),
      take(1) // Ensure the observable completes
    );
  }
}
